import React from "react";

export interface TopicInputProps {
  onSubmit: (topic: string) => void;
  isLoading?: boolean;
}

export const TopicInput: React.FC<TopicInputProps> = ({
  onSubmit,
  isLoading = false,
}) => {
  const [topic, setTopic] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = topic.trim();
    if (trimmed && !isLoading) {
      onSubmit(trimmed);
    }
  };

  const suggestions = [
    "How do lungs work?",
    "How does the internet work?",
    "How does an engine work?",
    "Explain photosynthesis",
    "How does a CPU work?",
    "How do vaccines work?",
    "Explain DNA & genetics",
    "How does the solar system work?",
    "How does electricity work?",
    "How does machine learning work?",
    "How does blockchain work?",
    "How does the heart work?",
    "How do mountains form?",
    "Human anatomy basics",
    "Physics: forces & motion",
    "World geography & maps",
    "Business & economics 101",
    "Math essentials",
    "Animal kingdom & wildlife",
    "Energy sources explained",
    "How vehicles work",
    "Chemistry fundamentals",
    "Plant biology & growth",
    "Engineering structures",
    "People & relationships",
    "Emotions & ideas",
    "Black holes & cosmos",
    "Visual storytelling",
    "Daily life scenes",
    "Puzzles & games",
    "Cars, rockets & vehicles",
    "Gravity & forces of nature",
    "World culture & traditions",
    "Cell biology & viruses",
    "Character animation",
    "Abstract concepts & time",
  ];

  return (
    <div className="topic-input-container">
      <div className="topic-input-hero">
        {/* Logo */}
        <div className="logo">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <rect width="48" height="48" rx="12" fill="#6366f1" />
            <path d="M14 16L24 12L34 16V28L24 32L14 28V16Z" fill="#818cf8" stroke="#c7d2fe" strokeWidth="1.5" />
            <path d="M24 12V32" stroke="#c7d2fe" strokeWidth="1.5" />
            <path d="M14 16L24 20L34 16" stroke="#c7d2fe" strokeWidth="1.5" />
            <circle cx="24" cy="22" r="3" fill="#e0e7ff" />
          </svg>
          <h1 className="logo-text">LearnEngine</h1>
        </div>

        <p className="hero-subtitle">
          Explanations you can <em>run</em>, not just read.
        </p>

        {/* Input form */}
        <form className="topic-form" onSubmit={handleSubmit}>
          <div className="topic-input-wrapper">
            <svg className="topic-input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="9" cy="9" r="6" />
              <path d="M13.5 13.5L17 17" strokeLinecap="round" />
            </svg>
            <input
              ref={inputRef}
              className="topic-input"
              type="text"
              placeholder="What do you want to understand?"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              disabled={isLoading}
            />
            <button
              className="topic-submit-btn"
              type="submit"
              disabled={!topic.trim() || isLoading}
            >
              {isLoading ? (
                <div className="spinner" />
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M3 10h12M11 6l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          </div>
        </form>

        {/* Suggestions */}
        <div className="topic-suggestions">
          <span className="suggestions-label">Try:</span>
          {suggestions.map((s) => (
            <button
              key={s}
              className="suggestion-chip"
              onClick={() => {
                setTopic(s);
                if (!isLoading) onSubmit(s);
              }}
              disabled={isLoading}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
